import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { apiFetch } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';
import SortIcon from './SortIcon';
import { useSortConfig } from '../hooks/useSortConfig';

const ChevronIcon = ({ isExpanded }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
    >
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);

const Skel = ({ className = '' }) => (
    <span className={`inline-block align-middle bg-gray-200 rounded animate-pulse ${className}`} aria-hidden="true" />
);

const statusPillClass = (status) => {
    if (status?.includes('Completed')) return 'text-green-600 bg-green-50 border-green-100';
    if (status === 'Failed') return 'text-red-600 bg-red-50 border-red-100';
    return 'text-amber-700 bg-amber-50 border-amber-100';
};

const SKELETON_ROWS = 3;

function OrderHistory({ t }) {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const requestIdRef = useRef(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ totalPages: 1, totalCount: 0 });
    const [sortConfig, toggleSort] = useSortConfig({ key: 'createdAt', direction: 'descending' }, ['ascending', 'descending']);
    const [expandedOrderId, setExpandedOrderId] = useState(null);

    const apiUrl = process.env.REACT_APP_API_URL || '';
    const ITEMS_PER_PAGE = 20;

    // table-fixed: these widths fit the header labels, so columns never shift when skeleton rows become data.
    // Status and Actions share the rest and wrap, so the table fits a ~680px container without scrolling.
    const columns = [
        { key: 'id', label: t.invoice_number, sortable: true, width: 'w-36' },
        { key: 'createdAt', label: t.date, sortable: true, width: 'w-28' },
        { key: 'status', label: t.status, sortable: true },
        { key: 'totalPrice', label: t.total_amount, sortable: true, width: 'w-[132px]' },
        { key: 'actions', label: t.actions, sortable: false, align: 'right' }
    ];

    const fetchOrders = useCallback(async (page = 1, sort = sortConfig) => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        let customerId = null;

        const storedCustomer = localStorage.getItem('selectedCustomer');
        if (storedCustomer) {
            const selectedCustomer = JSON.parse(storedCustomer);
            customerId = selectedCustomer.customerId;
        }

        if (!customerId) {
            setIsLoading(false);
            return;
        }

        try {
            const sortOrderParam = sort.direction === 'ascending' ? 'asc' : 'desc';
            const sortByParam = sort.key;

            const response = await apiFetch(
                `${apiUrl}/api/customers/${customerId}/orders?page=${page}&limit=${ITEMS_PER_PAGE}&sortBy=${sortByParam}&sortOrder=${sortOrderParam}`);
            if (!response.ok) {
                console.error(`Error ${response.status}: ${response.statusText}`);
                throw new Error('Failed to fetch orders');
            }
            const data = await response.json();
            // The headers stay clickable while loading, so a slower earlier sort must not overwrite a newer one.
            if (requestId !== requestIdRef.current) return;
            const fetchedOrders = data.orders || [];
            setOrders(fetchedOrders);
            setPagination(data.pagination || { totalPages: 1, totalCount: 0 });

            if (fetchedOrders.length > 0) {
                const latestDate = fetchedOrders.reduce((max, order) => {
                    const d = new Date(order.createdAt).getTime();
                    return d > max ? d : max;
                }, 0);
                localStorage.setItem(`lastViewedOrdersAt_${customerId}`, new Date(latestDate).toISOString());
            } else {
                localStorage.setItem(`lastViewedOrdersAt_${customerId}`, new Date().toISOString());
            }
            window.dispatchEvent(new Event('ordersViewed'));
        } catch (error) {
            console.error('Error fetching orders:', error);
            if (requestId === requestIdRef.current) toast.error(t.error_title || "Error");
        } finally {
            if (requestId === requestIdRef.current) setIsLoading(false);
        }
    }, [apiUrl, t, sortConfig]);

    // Initial fetch
    useEffect(() => {
        fetchOrders(currentPage, sortConfig);
    }, [fetchOrders, currentPage, sortConfig]);

    // Handle Stripe payment redirect messages
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (query.get('payment') === 'success') {
            toast.success('Payment successful!');
            window.history.replaceState(null, '', window.location.pathname);
        } else if (query.get('payment') === 'cancelled') {
            toast.error('Payment cancelled.');
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const requestSort = (key) => {
        toggleSort(key);
        setCurrentPage(1); // Reset to first page on sort
        setExpandedOrderId(null); // Collapse any expanded row
    };

    const [payingOrderId, setPayingOrderId] = useState(null);
    const [copiedOrderId, setCopiedOrderId] = useState(null);

    const handleCopyPaymentLink = (orderOrId, e) => {
        if (e) {
            e.stopPropagation();
        }
        const orderId = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
        const token = (typeof orderOrId === 'object' && orderOrId.paymentToken) ? orderOrId.paymentToken : orderId;
        const paymentUrl = `${window.location.origin}/payment/${token}`;
        const onSuccess = () => {
            setCopiedOrderId(orderId);
            toast.success(t.payment_link_copied || 'Betaallink gekopieerd naar klembord!');
            setTimeout(() => setCopiedOrderId(null), 2000);
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(paymentUrl).then(onSuccess).catch(() => fallbackCopy(paymentUrl));
        } else {
            fallbackCopy(paymentUrl);
        }

        function fallbackCopy(text) {
            try {
                const textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (successful) {
                    onSuccess();
                } else {
                    toast.error('Failed to copy');
                }
            } catch (err) {
                console.error('Fallback copy failed', err);
                toast.error('Failed to copy');
            }
        }
    };

    const handlePayWithStripe = async (orderId, e) => {
        if (e) {
            e.stopPropagation();
        }
        setPayingOrderId(orderId);
        try {
            const res = await apiFetch(`${apiUrl}/api/orders/${orderId}/pay`, {
                method: 'POST'
            });
            const data = await res.json();
            if (res.ok && data.url) {
                window.location.href = data.url;
            } else {
                toast.error(errorText(data, t, 'Failed to initialize payment.'));
            }
        } catch (error) {
            console.error('Error generating payment link:', error);
            toast.error(error.message || 'An unexpected error occurred.');
        } finally {
            setPayingOrderId(null);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">{t.order_history}</h2>
            </div>

            {orders.length === 0 && !isLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                    {t.no_orders_found}
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-lg shadow-sm overflow-x-auto border border-gray-200">
                        <table className="w-full min-w-[680px] table-fixed divide-y divide-gray-200">
                            <thead className="bg-primary">
                                <tr>
                                    <th scope="col" className="w-8 py-3" />
                                    {columns.map((col) => (
                                        <th
                                            key={col.key}
                                            scope="col"
                                            onClick={() => col.sortable && requestSort(col.key)}
                                            aria-sort={sortConfig.key === col.key ? sortConfig.direction : undefined}
                                            className={`px-3 py-3 whitespace-nowrap text-xs font-semibold text-white uppercase tracking-wider ${col.width || ''} ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.sortable ? 'cursor-pointer select-none group' : ''}`}
                                        >
                                            <span className="relative">
                                                {col.label}
                                                {col.sortable && (
                                                    <span className={`absolute left-full top-1/2 -translate-y-1/2 ml-1 flex ${sortConfig.key === col.key ? 'text-white' : 'text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                                                        <SortIcon direction={sortConfig.key === col.key ? sortConfig.direction : null} />
                                                    </span>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200" aria-busy={isLoading}>
                                {isLoading && Array.from({ length: orders.length || SKELETON_ROWS }, (_, i) => (
                                    <tr key={`skeleton-${i}`}>
                                        <td className="pl-3 py-4"><Skel className="h-5 w-5" /></td>
                                        <td className="px-3 py-4"><Skel className="h-4 w-24" /></td>
                                        <td className="px-3 py-4"><Skel className="h-4 w-16" /></td>
                                        <td className="px-3 py-4"><Skel className="h-5 w-24 rounded-md" /></td>
                                        <td className="px-3 py-4"><Skel className="h-4 w-16" /></td>
                                        <td className="px-3 py-4 text-right"><Skel className="h-4 w-24" /></td>
                                    </tr>
                                ))}
                                {!isLoading && orders.map((order) => (
                                    <React.Fragment key={order.id}>
                                        <tr
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                                        >
                                            <td className="pl-3 py-4 text-gray-500">
                                                <ChevronIcon isExpanded={expandedOrderId === order.id} />
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-semibold text-primary">
                                                {order.id}
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {new Date(order.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-3 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${statusPillClass(order.status)}`}>
                                                        {order.status}
                                                    </span>
                                                    {(order.status === 'Failed' || order.status === 'Pending Payment') && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleCopyPaymentLink(order, e)}
                                                            title={t.copy_payment_link || 'Betaallink kopiëren'}
                                                            className="p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors focus:outline-none"
                                                        >
                                                            {copiedOrderId === order.id ? (
                                                                <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                                                € {order.totalPrice?.toFixed(2)}
                                            </td>
                                            <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                                                    {order.invoicePdfLink && (
                                                        <a
                                                            href={order.invoicePdfLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {t.view_invoice}
                                                        </a>
                                                    )}
                                                    {(order.stripeReceiptUrl || order.receiptUrl) && (
                                                        <a
                                                            href={order.stripeReceiptUrl || order.receiptUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline bg-indigo-50 border border-indigo-100 rounded transition-colors"
                                                            title={t?.view_receipt || "Bekijk betaalbewijs"}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                            <span>{t?.receipt || 'Betaalbewijs'}</span>
                                                        </a>
                                                    )}
                                                    {!order.invoicePdfLink && !(order.stripeReceiptUrl || order.receiptUrl) && (
                                                        (order.status === 'Failed' || order.status === 'Pending Payment') ? (
                                                            <button
                                                                onClick={(e) => handlePayWithStripe(order.id, e)}
                                                                disabled={payingOrderId === order.id}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#6772E5] text-white text-xs font-semibold rounded hover:bg-[#5469d4] shadow-sm transition-all disabled:opacity-50"
                                                            >
                                                                {payingOrderId === order.id ? (
                                                                    <>
                                                                        <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        <span>{t.paying || 'Processing...'}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                                                                            <path d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
                                                                        </svg>
                                                                        <span>{t.pay || 'Pay'}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-400">{t.not_applicable}</span>
                                                        )
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Expanded Detail Row */}
                                        {expandedOrderId === order.id && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={columns.length + 1} className="px-6 py-6 bg-gray-50/50">
                                                    <div className="text-sm">
                                                        {(order.afasError || order.error) && (
                                                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 font-medium text-xs flex items-start gap-2">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 mt-0.5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                                <div className="break-all whitespace-pre-wrap">
                                                                    {order.afasError || order.error}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                                                            <div>
                                                                <h4 className="font-semibold text-gray-700 mb-1">{t.order_details || 'Order Details'}</h4>
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium text-gray-700">{t.ordered_by || 'Ordered By'}:</span>{' '}
                                                                    {order.orderUser || '-'}
                                                                </p>
                                                            </div>
                                                            {(order.stripeReceiptUrl || order.receiptUrl) && (
                                                                <a
                                                                    href={order.stripeReceiptUrl || order.receiptUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200 rounded-md transition-colors shadow-sm"
                                                                    title={t?.view_receipt || "Bekijk betaalbewijs"}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                    </svg>
                                                                    <span>{t?.receipt || 'Betaalbewijs'}</span>
                                                                </a>
                                                            )}
                                                        </div>
                                                        {order.cartItems && order.cartItems.length > 0 ? (
                                                            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                                                                <table className="w-full text-sm text-left">
                                                                    <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] font-bold tracking-wider">
                                                                        <tr>
                                                                            <th className="px-4 py-2">{t.name || 'Name'}</th>
                                                                            <th className="px-4 py-2">{t.description || 'Description'}</th>
                                                                            <th className="px-4 py-2 text-right">{t.price || 'Price'}</th>
                                                                            <th className="px-4 py-2 text-right">{t.quantity || 'Quantity'}</th>
                                                                            <th className="px-4 py-2 text-right">{t.subtotal || 'Subtotal'}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100">
                                                                        {order.cartItems.map((item, idx) => (
                                                                            <tr key={idx} className="hover:bg-gray-50/80 transition-colors align-top">
                                                                                <td className="px-4 py-2 text-gray-900">
                                                                                    <div>{item.name}</div>
                                                                                    {item.item_subtitle && (
                                                                                        <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                                                            {item.item_subtitle}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-4 py-2 text-gray-500">{item.description || '-'}</td>
                                                                                <td className="px-4 py-2 whitespace-nowrap text-gray-900 text-right">€ {item.price?.toFixed(2)}</td>
                                                                                <td className="px-4 py-2 whitespace-nowrap text-gray-900 text-right">{item.quantity}</td>
                                                                                <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900 text-right">€ {(item.price * item.quantity)?.toFixed(2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-gray-400">{t.no_items || 'No items found'}</p>
                                                        )}
                                                        {/* Order Summary */}
                                                        <div className="flex justify-end mt-4">
                                                            <div className="w-64">
                                                                <div className="flex justify-between py-1 text-sm text-gray-600">
                                                                    <span>{t.subtotal || 'Subtotal'}</span>
                                                                    <span>€ {order.subtotal?.toFixed(2)}</span>
                                                                </div>
                                                                {order.paypalFee > 0 && (
                                                                    <div className="flex justify-between py-1 text-sm text-gray-600">
                                                                        <span>{t.paypal_fee || 'PayPal Fee (3%)'}</span>
                                                                        <span>€ {order.paypalFee?.toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                                <div className="flex justify-between py-1 text-sm text-gray-600">
                                                                    <span>{t.vat_amount || 'VAT'}</span>
                                                                    <span>€ {order.vatAmount?.toFixed(2)}</span>
                                                                </div>
                                                                <div className="flex justify-between py-1 text-sm font-semibold text-gray-900 border-t border-gray-300 mt-1 pt-1">
                                                                    <span>{t.total_amount || 'Total'}</span>
                                                                    <span>€ {order.totalPrice?.toFixed(2)}</span>
                                                                </div>

                                                                {(order.status === 'Failed' || order.status === 'Pending Payment') && (
                                                                    <div className="mt-4 flex justify-end items-center gap-3">
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => handleCopyPaymentLink(order, e)}
                                                                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded text-sm font-medium shadow-sm transition-all"
                                                                        >
                                                                            {copiedOrderId === order.id ? (
                                                                                <>
                                                                                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                                                    </svg>
                                                                                    <span className="text-emerald-700 font-semibold">{t.copied || 'Gekopieerd!'}</span>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                                                    </svg>
                                                                                    <span>{t.copy_payment_link || 'Betaallink kopiëren'}</span>
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => handlePayWithStripe(order.id, e)}
                                                                            disabled={payingOrderId === order.id}
                                                                            className={`transition-all outline-none ${payingOrderId === order.id
                                                                                ? 'animated-gradient-btn-wrapper cursor-wait border-0 p-0 block'
                                                                                : 'flex items-center gap-2 px-4 py-2 bg-[#6772E5] text-white rounded hover:bg-[#5469d4] disabled:opacity-50 text-sm font-medium shadow-sm'
                                                                                }`}
                                                                        >
                                                                            {payingOrderId === order.id ? (
                                                                                <div className="animated-gradient-btn-inner flex items-center justify-center gap-2 px-4 py-1.5 text-gray-700 font-medium">
                                                                                    <svg className="animate-spin h-4 w-4 text-[#6772E5]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                    </svg>
                                                                                    {t.paying || t.loading || 'Loading...'}
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                                                                        <path d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" />
                                                                                    </svg>
                                                                                    {t.pay_with_stripe || 'Pay with Stripe'}
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 sm:px-6">
                            <div className="text-sm text-gray-700">
                                {t.showing || 'Showing'} <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> - <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, pagination.totalCount)}</span> {t.of || 'of'} <span className="font-medium">{pagination.totalCount}</span>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-primary text-white hover:bg-opacity-90'
                                        }`}
                                >
                                    {t.previous || 'Previous'}
                                </button>
                                <span className="px-3 py-1 text-sm text-gray-700">
                                    {currentPage} / {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === pagination.totalPages}
                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-primary text-white hover:bg-opacity-90'
                                        }`}
                                >
                                    {t.next || 'Next'}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default OrderHistory;

